import { z } from '@harvestiq/zod';
import { zDecimalNullable, zDecimalRequired } from '@harvestiq/zod';
import {
  InsureIQCoverageStatus,
  InsureIQPolicyTypes,
} from '@harvestiq/constants';
import { getCoveragesRequestSchema } from './get-coverages';
import { lrpCommodityCodeTypeCodePairSchema } from './lrp-commodity-code-type-code-pair';
import { lgmCommodityCodeTypeCodePairSchema } from './lgm-commodity-code-type-code-pair';

export const getLrpCoveragesReportRequestSchema = getCoveragesRequestSchema
  .pick({
    entityIds: true,
    salesEffectiveDateStart: true,
    salesEffectiveDateEnd: true,
    endDateStart: true,
    endDateEnd: true,
    statuses: true,
  })
  .extend({
    type: z.literal(InsureIQPolicyTypes.LRP),
    commodityCodeTypeCodePairs: z
      .array(lrpCommodityCodeTypeCodePairSchema)
      .optional(),
  });
export type GetLrpCoveragesReportRequest = z.infer<
  typeof getLrpCoveragesReportRequestSchema
>;

export const getLgmCoveragesReportRequestSchema = getCoveragesRequestSchema
  .pick({
    entityIds: true,
    salesEffectiveDateStart: true,
    salesEffectiveDateEnd: true,
    endDateStart: true,
    endDateEnd: true,
    statuses: true,
  })
  .extend({
    type: z.literal(InsureIQPolicyTypes.LGM),
    commodityCodeTypeCodePairs: z
      .array(lgmCommodityCodeTypeCodePairSchema)
      .optional(),
  });
export type GetLgmCoveragesReportRequest = z.infer<
  typeof getLgmCoveragesReportRequestSchema
>;

export const insureIQEmailCoveragesReportRequestSchema = z.object({
  ccMe: z.boolean(),
  emailNotes: z.string(),
  s3FileKey: z.string(),
  recipients: z.array(
    z.object({
      email: z.string().email(),
      name: z.string(),
    })
  ),
  type: z.nativeEnum(InsureIQPolicyTypes),
  sendPreview: z.boolean().optional(),
});

export type InsureIQEmailCoveragesReportRequest = z.infer<
  typeof insureIQEmailCoveragesReportRequestSchema
>;

export const insureIQEmailCoveragesReportSchema = z.object({
  emailNotes: z.string(),
  s3FileKey: z.string(),
  fileUrl: z.string(),
  type: z.nativeEnum(InsureIQPolicyTypes),
  insured: z.string(),
});

export type InsureIQEmailCoveragesReport = z.infer<
  typeof insureIQEmailCoveragesReportSchema
>;

export const insureIQEmailCoveragesReportResponseSchema = z.object({
  data: z.array(insureIQEmailCoveragesReportSchema),
});

export type InsureIQEmailCoveragesReportResponse = z.infer<
  typeof insureIQEmailCoveragesReportResponseSchema
>;

export const endorsementListTabWorksheetSchema = z.object({
  endDate: z.string(),
  commodity: z.string(),
  type: z.string(),
  salesEffectiveDate: z.string(),
  status: z.nativeEnum(InsureIQCoverageStatus),
  insured: z.string(),
  agent: z.string(),
  numberofHead: z.number(),
  targetWeightPerCwt: z.number(),
  coveragePricePerCwt: z.number(),
  producerPremiumPerHead: z.number(),
  projectedOrActualEndingValuePerCwt: z.number(),
  projectedIndemnityPerHead: z.number(),
  netProjectedIndemnityPerHead: z.number(),
});
export type EndorsementListTabWorksheet = z.infer<
  typeof endorsementListTabWorksheetSchema
>;

export const endorsementMonthlyTabWorksheetSchema = z.object({
  commodity: z.string(),
  numberofHead: z.number(),
  endMonth: z.string(),
  numberOfEndorsements: z.number().optional(),
  totalTargetWeight: z.number(),
  totalCoveragePrice: z.number(),
  totalPremium: z.number(),
  totalProjectedOrActualEndingValue: z.number().nullable(),
  totalProjectedIndemnity: z.number().nullable(),
  totalNetProjectedIndemnity: z.number().nullable(),
});
export type EndorsementMonthlyTabWorksheet = z.infer<
  typeof endorsementMonthlyTabWorksheetSchema
>;

export const lrpCoveragesReportKpisSchema = z.object({
  totalEndorsements: zDecimalRequired(),
  totalHeadcount: zDecimalRequired(),
  totalWeightCwt: zDecimalRequired(),
  totalLiabilityAmount: zDecimalRequired(),
  totalProjectedRevenue: zDecimalRequired(),
  totalProjectedIndemnity: zDecimalRequired(),
  totalProducerPremium: zDecimalRequired(),
  totalNetProjectedIndemnity: zDecimalRequired(),
});

export type LrpCoveragesReportKpis = z.infer<
  typeof lrpCoveragesReportKpisSchema
>;

export const getLrpCoveragesReportSuccessResponseSchema = z.object({
  kpis: lrpCoveragesReportKpisSchema,
  s3FileKey: z.string(),
});
export type GetLrpCoveragesReportSuccessResponse = z.infer<
  typeof getLrpCoveragesReportSuccessResponseSchema
>;

export const getLrpCoveragesReportErrorResponseSchema = z.object({
  errors: z.array(z.string()),
});
export type GetLrpCoveragesReportErrorResponse = z.infer<
  typeof getLrpCoveragesReportErrorResponseSchema
>;

export const getLrpCoveragesReportResponseSchema =
  getLrpCoveragesReportSuccessResponseSchema.or(
    getLrpCoveragesReportErrorResponseSchema
  );
export type GetLrpCoveragesReportResponse = z.infer<
  typeof getLrpCoveragesReportResponseSchema
>;

export const lgmCoveragesReportEndorsementSchema = z.object({
  salesEffectiveDate: z.string(),
  commodity: z.string(),
  type: z.string(),
  status: z.nativeEnum(InsureIQCoverageStatus).or(z.literal('')),
  insured: z.string(),
  agent: z.string(),
  marketingMonth: z.string(),
  totalEndorsements: z.number().int().optional(),
  deductible: z.number().int().optional(),
  totalTargetMarketings: z.number().int(),
  actualsPublished: z.boolean(),
  totalExpectedGrossMargin: zDecimalRequired(),
  totalGrossMarginGuarantee: zDecimalRequired(),
  totalProjectedOrActualGrossMargin: zDecimalRequired(),
  totalBasePremium: zDecimalRequired(),
  totalSubsidy: zDecimalRequired(),
  totalProducerPremium: zDecimalRequired(),
  totalGrossMarginChange: zDecimalRequired(),
  totalProjectedIndemnity: zDecimalNullable(), // only use for Total row
  totalNetProjectedIndemnity: zDecimalNullable(), // only use for Total row
  totalAllocatedActualIndemnity: zDecimalNullable().optional(),
  totalAllocatedNetActualIndemnity: zDecimalNullable().optional(),
  totalAllocatedProjectedIndemnity: zDecimalNullable().optional(),
  totalAllocatedNetProjectedIndemnity: zDecimalNullable().optional(),
});
export type LgmCoveragesReportEndorsement = z.infer<
  typeof lgmCoveragesReportEndorsementSchema
>;

export const lgmCoveragesReportKpisSchema = z.object({
  totalEndorsements: z.number().int(),
  totalTargetMarketings: z.number().int(),
  totalExpectedGrossMargin: zDecimalRequired(),
  totalGrossMarginGuarantee: zDecimalRequired(),
  totalProjectedOrActualGrossMargin: zDecimalRequired(),
  totalBasePremium: zDecimalRequired(),
  totalSubsidy: zDecimalRequired(),
  totalProducerPremium: zDecimalRequired(),
  totalNetProducerPremium: zDecimalRequired(),
  totalProjectedIndemnity: zDecimalRequired(),
  totalNetProjectedIndemnity: zDecimalRequired(),
});

export type LgmCoveragesReportKpis = z.infer<
  typeof lgmCoveragesReportKpisSchema
>;

export const lgmCoveragesReportMonthlySchema =
  lgmCoveragesReportEndorsementSchema
    .pick({
      marketingMonth: true,
      commodity: true,
      type: true,
      status: true,
      insured: true,
      agent: true,
      totalTargetMarketings: true,
      totalEndorsements: true,
      totalProducerPremium: true,
    })
    .extend({
      totalAllocatedActualIndemnity: zDecimalNullable().optional(),
      totalAllocatedNetActualIndemnity: zDecimalRequired()
        .nullable()
        .optional(),
      totalAllocatedProjectedIndemnity: zDecimalRequired()
        .nullable()
        .optional(),
      totalAllocatedNetProjectedIndemnity: zDecimalRequired()
        .nullable()
        .optional(),
    });

export type LgmCoveragesReportMonthly = z.infer<
  typeof lgmCoveragesReportMonthlySchema
>;

export const getLgmCoveragesReportSuccessResponseSchema = z.object({
  kpis: lgmCoveragesReportKpisSchema,
  s3FileKey: z.string(),
});
export type GetLgmCoveragesReportSuccessResponse = z.infer<
  typeof getLgmCoveragesReportSuccessResponseSchema
>;

export const getLgmCoveragesReportErrorResponseSchema = z.object({
  errors: z.array(z.string()),
  details: z.string().optional(),
});
export type GetLgmCoveragesReportErrorResponse = z.infer<
  typeof getLgmCoveragesReportErrorResponseSchema
>;

export const getLgmCoveragesReportResponseSchema =
  getLgmCoveragesReportSuccessResponseSchema.or(
    getLgmCoveragesReportErrorResponseSchema
  );
export type GetLgmCoveragesReportResponse = z.infer<
  typeof getLgmCoveragesReportResponseSchema
>;
